import { createBrowserRouter } from 'react-router';

import { AppShell } from '../components/layout/app-shell.tsx';
import { AcceptInvitationPage } from '../pages/access/accept-invitation.tsx';
import { Enroll2faPage } from '../pages/access/enroll-2fa.tsx';
import { ForgotPasswordPage } from '../pages/access/forgot-password.tsx';
import { LoginPage } from '../pages/access/login.tsx';
import { ResetPasswordPage } from '../pages/access/reset-password.tsx';
import { Verify2faPage } from '../pages/access/verify-2fa.tsx';
import { AccountPage } from '../pages/account/account.tsx';
import { ContactDetailPage } from '../pages/contacts/detail.tsx';
import { EditContactPage, NewContactPage } from '../pages/contacts/form-pages.tsx';
import { ContactsPage } from '../pages/contacts/list.tsx';
import { ContactsTrashPage } from '../pages/contacts/trash.tsx';
import { AgencyPage } from '../pages/admin/agency.tsx';
import { PipelinesPage } from '../pages/admin/pipelines.tsx';
import { DealDetailPage } from '../pages/deals/detail.tsx';
import { DealsPage } from '../pages/deals/list.tsx';
import { AgendaPage } from '../pages/agenda/agenda.tsx';
import { ImportPage } from '../pages/imports/detail.tsx';
import { PrivacyRequestPage } from '../pages/admin/privacy-requests.tsx';
import { ImportsPage } from '../pages/imports/list.tsx';
import { ActivitiesTrashPage } from '../pages/agenda/trash.tsx';
import { ActivityTypesPage } from '../pages/admin/activity-types.tsx';
import { NewDealPage } from '../pages/deals/new.tsx';
import { DealsTrashPage } from '../pages/deals/trash.tsx';
import { PrivacyPage } from '../pages/admin/privacy.tsx';
import { SharedListingPage } from '../pages/public/shared-listing.tsx';
import { PropertyDetailPage } from '../pages/properties/detail.tsx';
import { EditPropertyPage, NewPropertyPage } from '../pages/properties/form-pages.tsx';
import { PropertiesPage } from '../pages/properties/list.tsx';
import { PropertiesTrashPage } from '../pages/properties/trash.tsx';
import { RolesPage } from '../pages/admin/roles.tsx';
import { ApiKeysPage } from '../pages/admin/api-keys.tsx';
import { LeadsSettingsPage } from '../pages/admin/leads.tsx';
import { WebhooksPage } from '../pages/admin/webhooks.tsx';
import { SecurityPage } from '../pages/admin/security.tsx';
import { UsersPage } from '../pages/admin/users.tsx';
import { HomePage } from '../pages/home.tsx';
import { RequirePermission } from './require-permission.tsx';
import { NotFoundPage } from '../pages/status-pages.tsx';
import { RequireSession } from './require-session.tsx';

export function createRouter() {
  return createBrowserRouter([
    { path: '/iniciar-sesion', element: <LoginPage /> },
    { path: '/verificacion', element: <Verify2faPage /> },
    { path: '/activar-2fa', element: <Enroll2faPage /> },
    { path: '/invitacion', element: <AcceptInvitationPage /> },
    { path: '/olvide-contrasena', element: <ForgotPasswordPage /> },
    { path: '/restablecer', element: <ResetPasswordPage /> },
    // Public: what a client opens from a share link (no account).
    { path: '/f/:token', element: <SharedListingPage /> },
    {
      element: <RequireSession />,
      children: [
        {
          element: <AppShell />,
          children: [
            { index: true, element: <HomePage /> },
            { path: 'mi-cuenta', element: <AccountPage /> },
            {
              element: <RequirePermission permission="records.view" />,
              children: [
                { path: 'contactos', element: <ContactsPage /> },
                { path: 'contactos/:id', element: <ContactDetailPage /> },
                { path: 'propiedades', element: <PropertiesPage /> },
                { path: 'propiedades/:id', element: <PropertyDetailPage /> },
                { path: 'negocios', element: <DealsPage /> },
                { path: 'negocios/:id', element: <DealDetailPage /> },
                { path: 'agenda', element: <AgendaPage /> },
              ],
            },
            {
              element: <RequirePermission permission="records.edit" />,
              children: [
                { path: 'contactos/nuevo', element: <NewContactPage /> },
                { path: 'contactos/:id/editar', element: <EditContactPage /> },
                { path: 'propiedades/nueva', element: <NewPropertyPage /> },
                { path: 'propiedades/:id/editar', element: <EditPropertyPage /> },
                { path: 'negocios/nuevo', element: <NewDealPage /> },
              ],
            },
            {
              element: <RequirePermission permission="records.delete" />,
              children: [
                { path: 'contactos/papelera', element: <ContactsTrashPage /> },
                { path: 'propiedades/papelera', element: <PropertiesTrashPage /> },
                { path: 'negocios/papelera', element: <DealsTrashPage /> },
                { path: 'agenda/papelera', element: <ActivitiesTrashPage /> },
              ],
            },
            {
              element: <RequirePermission permission="users.manage" />,
              children: [{ path: 'admin/usuarios', element: <UsersPage /> }],
            },
            {
              element: <RequirePermission permission="roles.manage" />,
              children: [{ path: 'admin/roles', element: <RolesPage /> }],
            },
            {
              // The integrations: same permission as the API (api_keys.manage).
              element: <RequirePermission permission="api_keys.manage" />,
              children: [
                { path: 'admin/claves-api', element: <ApiKeysPage /> },
                { path: 'admin/webhooks', element: <WebhooksPage /> },
              ],
            },
            {
              element: <RequirePermission permission="settings.manage" />,
              children: [
                { path: 'admin/seguridad', element: <SecurityPage /> },
                { path: 'admin/inmobiliaria', element: <AgencyPage /> },
                { path: 'admin/embudos', element: <PipelinesPage /> },
                { path: 'admin/leads', element: <LeadsSettingsPage /> },
                { path: 'admin/actividades', element: <ActivityTypesPage /> },
              ],
            },
            {
              element: <RequirePermission permission="data.import" />,
              children: [
                { path: 'importaciones', element: <ImportsPage /> },
                { path: 'importaciones/:id', element: <ImportPage /> },
              ],
            },
            {
              element: <RequirePermission permission="privacy.manage" />,
              children: [
                { path: 'admin/privacidad', element: <PrivacyPage /> },
                { path: 'admin/privacidad/solicitudes/:id', element: <PrivacyRequestPage /> },
              ],
            },
            { path: '*', element: <NotFoundPage /> },
          ],
        },
      ],
    },
  ]);
}
